/**
 * Subject-relevance helpers for library / Sketchfab model search.
 * Prevents unrelated assets from being accepted for queries like "car".
 */

const FILLER_WORDS = new Set([
  'a',
  'an',
  'the',
  'add',
  'create',
  'make',
  'get',
  'download',
  'find',
  'search',
  'from',
  'to',
  'into',
  'onto',
  'in',
  'on',
  'my',
  'our',
  'me',
  'please',
  'scene',
  'grid',
  'editor',
  'with',
  'some',
  'new',
  'one',
  'of',
  'for',
  'and',
  'or',
  'sketchfab',
  'library',
  'spatialgrid',
  'glb',
  'gltf',
  'mesh',
  'object',
  'asset',
  'file',
  '3d',
  'model',
  'models',
  'low',
  'poly',
  'game',
  'ready',
]);

/** Expand short category queries so "car" also matches "vehicle", etc. */
export const SUBJECT_SYNONYMS = {
  car: ['car', 'cars', 'vehicle', 'automobile', 'sedan', 'coupe', 'suv', 'truck'],
  vehicle: ['vehicle', 'car', 'automobile', 'truck', 'suv'],
  truck: ['truck', 'lorry', 'pickup'],
  bike: ['bike', 'bicycle', 'motorcycle', 'motorbike'],
  house: ['house', 'home', 'building', 'cottage', 'villa'],
  building: ['building', 'house', 'tower', 'skyscraper'],
  tree: ['tree', 'trees', 'plant', 'palm'],
  plant: ['plant', 'flower', 'tree', 'bush'],
  dog: ['dog', 'puppy', 'canine'],
  cat: ['cat', 'kitten', 'feline'],
  chair: ['chair', 'seat', 'stool'],
  table: ['table', 'desk'],
  lamp: ['lamp', 'light', 'lantern'],
  robot: ['robot', 'android', 'mech'],
  character: ['character', 'person', 'human', 'man', 'woman', 'boy', 'girl'],
  dragon: ['dragon'],
  furniture: ['furniture', 'sofa', 'chair', 'table', 'desk', 'couch'],
};

/**
 * Normalize a raw AI/user model query into a clean search subject.
 * "a car model" → "car", "3d glb" → "glb", "red sports car" → "red sports car"
 */
export function normalizeModelSearchQuery(raw) {
  let q = String(raw || '')
    .toLowerCase()
    .trim()
    .replace(/["'`]/g, ' ')
    .replace(/[^a-z0-9\s-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  if (!q) return '';

  const tokens = q.split(/\s+/).filter((t) => t && !FILLER_WORDS.has(t));
  if (!tokens.length) {
    // Pure filler like "3d model" / "glb" — keep a generic token
    if (/\b(glb|gltf)\b/.test(q)) return 'glb';
    if (/\bmodels?\b/.test(q)) return 'model';
    if (/\b3d\b/.test(q)) return '3d';
    return q;
  }

  return tokens.join(' ');
}

/**
 * Subject tokens used for relevance checks (includes synonyms for the head term).
 */
export function getSubjectTokens(query) {
  const normalized = normalizeModelSearchQuery(query);
  if (!normalized) return [];

  const parts = normalized.split(/\s+/).filter(Boolean);
  const tokens = new Set(parts);

  // Expand synonyms for single-word categories and the last word of multi-word queries
  const heads = parts.length === 1 ? [parts[0]] : [parts[parts.length - 1], parts[0]];
  heads.forEach((head) => {
    const syns = SUBJECT_SYNONYMS[head];
    if (syns) syns.forEach((s) => tokens.add(s));
  });

  return [...tokens].filter((t) => t.length > 1);
}

function escapeRegExp(s) {
  return String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Whole-word (or strong) match of any subject token in a haystack string.
 */
export function haystackMatchesTokens(haystack, tokens) {
  const hay = String(haystack || '').toLowerCase();
  if (!hay || !tokens?.length) return false;

  return tokens.some((token) => {
    const t = String(token).toLowerCase();
    if (t.length <= 2) return hay === t;
    // Whole word boundary — avoids "car" matching inside unrelated words
    try {
      return new RegExp(`\\b${escapeRegExp(t)}\\b`, 'i').test(hay);
    } catch {
      return hay.includes(t);
    }
  });
}

/**
 * True when a library/Sketchfab item is actually about the requested subject.
 * Requires a hit in name or tags (description alone is too noisy).
 */
export function itemMatchesSubject(item, query) {
  const tokens = getSubjectTokens(query);
  if (!tokens.length) return false;

  const name = String(item?.name || item?.title || '');
  const tags = Array.isArray(item?.tags)
    ? item.tags.map((t) => (typeof t === 'string' ? t : t?.name || '')).join(' ')
    : String(item?.tags || '');
  const subtype = String(item?.subtype || item?.category || '');

  const primary = `${name} ${tags} ${subtype}`;
  if (haystackMatchesTokens(primary, tokens)) return true;

  // Multi-word: require every non-synonym original token in name+tags
  const original = normalizeModelSearchQuery(query)
    .split(/\s+/)
    .filter(Boolean);
  if (original.length > 1) {
    return original.every((t) => haystackMatchesTokens(primary, [t]));
  }

  return false;
}

/**
 * Relevance score 0–100 focused on subject accuracy (name/tags first).
 */
export function subjectRelevanceScore(item, query) {
  const tokens = getSubjectTokens(query);
  if (!tokens.length || !item) return 0;

  const name = String(item.name || item.title || '').toLowerCase();
  const tags = Array.isArray(item.tags)
    ? item.tags.map((t) => (typeof t === 'string' ? t : t?.name || '')).join(' ').toLowerCase()
    : String(item.tags || '').toLowerCase();
  const subtype = String(item.subtype || '').toLowerCase();
  const description = String(item.description || '').toLowerCase();
  const normalized = normalizeModelSearchQuery(query);

  let score = 0;

  if (name === normalized) score += 100;
  else if (name.startsWith(normalized)) score += 80;
  else if (haystackMatchesTokens(name, tokens)) score += 60;
  else if (haystackMatchesTokens(tags, tokens)) score += 40;
  else if (haystackMatchesTokens(subtype, tokens)) score += 30;
  else if (haystackMatchesTokens(description, tokens)) score += 10;
  else return 0;

  // Prefer concise names that lead with the subject
  const firstToken = normalized.split(/\s+/)[0];
  if (firstToken && name.startsWith(firstToken)) score += 10;

  return score;
}
